/**
 * Real participant client over `/v1` (docs/VOCABULARY.md route surface).
 * TODO(api-client): swap for `@windrop/api-client` when it ships; keep the `WindropClient` method names.
 */
import type {
  CheckoutDto,
  ClaimCatalogDto,
  ClaimSessionResponse,
  ClaimStatusDto,
  ChannelDto,
  CurrentClaimDto,
  EntryReceiptDto,
  EntryStatusDto,
  QuickPickResponse,
  FairnessCommitmentDto,
  GoRedirectResponseDto,
  HoldDto,
  PaymentStatusDto,
  PublicLotteryDto,
  ReferralResolutionDto,
  ResultsDto,
  RevealResultDto,
  SupportCaseDto,
  TaskCompletionDto,
  WinLinkDto,
} from '@windrop/contracts';
import { claimMutation } from './claims-http';
import { beacon, http } from './http';
import type { WindropClient } from './types';

export function createHttpClient(): WindropClient {
  return {
    getLottery: (slug, opts) => http<PublicLotteryDto>(`/v1/l/${encodeURIComponent(slug)}`, opts),
    createEntry: (slug, body, opts) => http(`/v1/l/${encodeURIComponent(slug)}/entries`, { ...opts, body }),
    getEntryReceipt: (entryId, opts) =>
      http<EntryReceiptDto>(`/v1/entries/${encodeURIComponent(entryId)}/receipt`, opts),
    getEntryStatus: (entryId, opts) =>
      http<EntryStatusDto>(`/v1/entries/${encodeURIComponent(entryId)}/status`, opts),
    quickPick: (slug, body, opts) =>
      http<QuickPickResponse>(`/v1/l/${encodeURIComponent(slug)}/quick-pick`, {
        ...opts,
        method: 'POST',
        body,
      }),
    initializePayment: (body, opts) => http<CheckoutDto>('/v1/payments/initialize', { ...opts, body }),
    getPaymentStatus: (paymentId, opts) =>
      http<PaymentStatusDto>(`/v1/payments/${encodeURIComponent(paymentId)}/status`, opts),
    completeTask: (taskId, body, opts) =>
      http<TaskCompletionDto>(`/v1/engagement/tasks/${encodeURIComponent(taskId)}/complete`, {
        ...opts,
        body,
      }),
    linkChannel: (body, opts) => http<ChannelDto>('/v1/participants/me/channels', { ...opts, body }),
    resolveReferral: (code, opts) => http<ReferralResolutionDto>(`/v1/r/${encodeURIComponent(code)}`, opts),
    getResults: (slug, opts) =>
      http<ResultsDto>(`/v1/l/${encodeURIComponent(slug)}/results`, {
        ...opts,
        ...(opts?.entryId ? { query: { entryId: opts.entryId } } : {}),
      }),
    getFairness: (drawId, opts) =>
      http<FairnessCommitmentDto>(`/v1/draws/${encodeURIComponent(drawId)}/fairness`, opts),
    getReveal: (entryId, opts) => {
      const { slug: _slug, ...rest } = opts ?? {};
      return http<RevealResultDto>(`/v1/entries/${encodeURIComponent(entryId)}/reveal`, rest);
    },
    openClaimSession: (token, opts) =>
      http<ClaimSessionResponse>(`/v1/claims/${encodeURIComponent(token)}/session`, {
        ...opts,
        method: 'POST',
      }),
    getCurrentClaim: (opts) => http<CurrentClaimDto>('/v1/claims/current', opts),
    getClaimCatalog: (opts) => http<ClaimCatalogDto>('/v1/claims/current/catalog', opts),
    holdVariant: (body, opts) => claimMutation<HoldDto>('/v1/claims/current/hold', body, opts),
    submitClaim: (body, opts) => claimMutation<ClaimStatusDto>('/v1/claims/current/submit', body, opts),
    getClaimStatus: (opts) => http<ClaimStatusDto>('/v1/claims/current/status', opts),
    getWinLink: (winId, query, opts) =>
      http<WinLinkDto>(`/v1/w/${encodeURIComponent(winId)}`, { ...opts, query }),
    getGoRedirect: (winId, platform, opts) =>
      http<GoRedirectResponseDto>(
        `/v1/go/${encodeURIComponent(winId)}/${encodeURIComponent(platform)}`,
        opts,
      ),
    createSupportCase: (body, opts) => http<SupportCaseDto>('/v1/support/cases', { ...opts, body }),
    getSupportCase: (caseRef, opts) =>
      http<SupportCaseDto>(`/v1/support/cases/${encodeURIComponent(caseRef)}`, opts),
    postAnalyticsEvents: (body, opts) => beacon('/v1/analytics/events', body, opts?.beacon),
    postVitals: (body, opts) => beacon('/v1/rum/vitals', body, opts?.beacon),
    extendHold: (opts) => claimMutation<HoldDto>('/v1/claims/current/hold/extend', {}, opts),
    presignEvidence: (body, opts) => claimMutation('/v1/claims/current/evidence/presign', body, opts),
    finalizeEvidence: (uploadId, opts) =>
      claimMutation('/v1/claims/current/evidence/finalize', { uploadId }, opts),
    saveClaimDraft: (body, opts) =>
      claimMutation('/v1/claims/current/draft', body, { ...opts, method: 'PUT' }),
    appealClaim: (body, opts) => claimMutation<ClaimStatusDto>('/v1/claims/current/appeal', body, opts),
  };
}
